import { useQueryParams } from '@calendly/hooks'
import { useSelector } from 'react-redux'
import detect from 'detect.js'
import { useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { countryCodeSelector } from 'selectors'
import { isCookiesAllowedSelector } from 'selectors/gdpr'
import { eventService } from 'services/analytics/event_service'
import { currentUserSelector } from 'selectors/user'
import { isEmbeddedSelector } from 'selectors/embed'
import { bookingLinkService } from 'services/analytics/booking_link_service'
import { customDomainSelector } from 'selectors/custom_domain'

// Module-level dedup: survives transient remounts without double-firing,
// cleared on route navigation so back-button revisits still fire.
const dispatchedKeys = new Set()

// Calls made before OneTrust consent has loaded into Redux end up gated by
// `allowed=false`; queue them here and flush once consent resolves.
const pendingDispatches = new Map()

const sha256 = async (value) => {
  if (typeof window.crypto.subtle === 'undefined') { return Promise.resolve('') }
  const msgUint8 = new TextEncoder().encode(value)
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export const useSendSegmentEvent = () => {
  const history = useHistory()
  const { embed_type: embedType, utm_content: utmContent, utm_source: utmSource, preview_source: previewSource } = useQueryParams()
  const allowed = useSelector(isCookiesAllowedSelector('performance')) && embedType !== 'preview'
  const currentUser = useSelector(currentUserSelector)
  const countryCode = useSelector(countryCodeSelector)
  const isEmbedded = useSelector(isEmbeddedSelector)
  const ua = detect.parse(navigator.userAgent)
  const utmContentValue = typeof utmContent === 'string' ? utmContent : null
  const utmSourceValue = typeof utmSource === 'string' ? utmSource : null
  const bookingLinkHost = window.location.origin
  const { isCustomDomain, linkDomain } = bookingLinkService.linkDomainData(
    bookingLinkHost,
    useSelector(customDomainSelector).default_domain
  )

  // Only pathname changes clear dedup; query-only replaces (e.g. calendar URL
  // sync) keep it intact so the same page doesn't re-fire on each param update.
  useEffect(() => {
    let prevPath = history.location.pathname
    return history.listen((loc) => {
      if (loc.pathname === prevPath) return
      prevPath = loc.pathname
      dispatchedKeys.clear()
    })
  }, [history])

  // Once OneTrust consent flips `allowed` to true, flush any dispatches that
  // were queued before consent loaded.
  useEffect(() => {
    if (!allowed || !window.analytics || pendingDispatches.size === 0) return
    const drainable = Array.from(pendingDispatches.values())
    pendingDispatches.clear()
    drainable.forEach((fire) => fire())
  }, [allowed])

  return useCallback((ownerType, owningUser, ownerId, bookingPageType, code) => {
    const dedupeKey = `${ownerId}:${bookingPageType}`
    if (dispatchedKeys.has(dedupeKey)) return

    const queuedHrefSnapshot = window.location.href

    const fire = () => {
      dispatchedKeys.add(dedupeKey)

      const hasAnonymousId = typeof (window.analytics.user) === 'function' && typeof (window.analytics.user().anonymousId) === 'function'

      sha256(queuedHrefSnapshot).then((hash) => eventService.dispatch({
        url: '/api/booking/analytics/track',
        name: 'Booking Page Viewed',
        version: 1,
        dependencies: { user_id: owningUser.id },
        entities: ['user', 'organization'],
        properties: {
          is_owner_session: currentUser?.uuid === owningUser.uuid,
          is_calendly_user: !!(currentUser?.uuid),
          is_embedded: isEmbedded,
          is_preview: !!previewSource,
          preview_source: previewSource,
          invitee_calendly_user_id: undefined,
          invitee_calendly_user_uuid: currentUser?.uuid,
          invitee_country_code: countryCode,
          invitee_browser: ua.browser.name,
          invitee_device: `${ua.device.name} ${ua.os.name}`,
          booking_page_type: bookingPageType,
          owner_type: ownerType,
          owner_id: ownerId,
          invitee_anonymous_id: hasAnonymousId ? window.analytics.user().anonymousId() : '',
          utm_content: utmContentValue,
          utm_source: utmSourceValue,
          custom_domain: isCustomDomain,
          domain: linkDomain,
          url_hashed: hash,
          is_disabled: ['event_type_deactivated', 'event_type_deleted'].includes(code)
        }
      })).catch(() => dispatchedKeys.delete(dedupeKey))
    }

    if (!allowed || !window.analytics) {
      pendingDispatches.set(dedupeKey, fire)
      return
    }

    fire()
  }, [
    allowed,
    countryCode,
    currentUser?.uuid,
    isCustomDomain,
    isEmbedded,
    linkDomain,
    previewSource,
    ua.browser.name,
    ua.device.name,
    ua.os.name,
    utmContentValue,
    utmSourceValue
  ])
}
